export type AuthenticationToken = {
  token: string
  expires_at: number
}

export type AuthenticationState =
  | 'unauthenticated'
  | 'authenticating'
  | 'authenticated'
  | 'reauthenticating'

export interface AuthenticationServiceInterface {
  /**
   * Retreives the current authentication state
   */
  get state(): AuthenticationState
  /**
   * Retreives an authentication token or returns `null` if not authenticated
   */
  getToken(): Promise<string | null>
}

/**
 * Emits events related to the lifecycle of an authenticated session.
 */
export interface AuthenticationEventSource {
  /**
   * Registers a listener that is invoked whenever the authentication state
   * changes.  The returned function removes the listener when it is no longer
   * needed.
   *
   * @param listener Called with the latest authentication state.
   * @returns A function that unsubscribes the listener.
   */
  onStateChange(listener: (state: AuthenticationState) => void): () => void
}

/**
 * Emits events related to the lifecycle of a game session.
 */
export interface InstantGameSessionEventSource {
  /**
   * Registers a listener that is invoked whenever a game session completes
   * (i.e. player finishes a game). The returned function removes the listener
   * when it is no longer needed.
   *
   * @param listener Called after a session has completed.
   * @returns A function that unsubscribes the listener.
   */
  onSessionComplete(listener: () => void): () => void
}

export const AsyncStateValue = {
  Pending: 'pending',
  Resolved: 'resolved',
  Rejected: 'rejected',
} as const

export type AsyncState = (typeof AsyncStateValue)[keyof typeof AsyncStateValue]
